import { t } from 'ember-intl';
import { PhFilePdf } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/** One prior-step document plus the translation key naming the step it came from. */
export interface PriorUploadRow {
  asset: TasAssetModel;
  /** `null` for an unrecognized classification — the label is then omitted. */
  labelKey: string | null;
}

interface PriorUploadsListSignature {
  Args: {
    /** Live uploads from earlier approval steps. Empty → render nothing. */
    rows: PriorUploadRow[];
  };
  Element: HTMLDivElement;
}

/**
 * "Previously uploaded" history below the document rows: every live document on
 * this application that belongs to an EARLIER approval step.
 *
 * The categorized rows only ever render the current step's classification, so
 * when a company moved onto this surface the employee lost sight of every
 * course-approval and admin-lifecycle document they had already uploaded — the
 * legacy free-form uploader listed all of them, labeled by step, and nothing
 * replaced that view.
 *
 * Deliberately view-only: no discard and no "file under…". Prior-step evidence
 * has already been approved, so letting the evidence step unpick it would be a
 * footgun — and it must never satisfy a completion requirement, which is why
 * these rows sit outside the required-documents tally entirely.
 */
const PriorUploadsList: TOC<PriorUploadsListSignature> = <template>
  {{#if @rows.length}}
    <div class="tio-col-extra-tight" data-test-prior-uploads ...attributes>
      <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
        {{t "tas.attachments.previous_uploads"}}
      </h4>
      <p class="text-xs tio-fg-muted">
        {{t "tas.attachments.previous_uploads_hint"}}
      </p>
      <div class="tio-col-extra-tight">
        {{#each @rows as |row|}}
          {{! Deliberately lighter than a DocumentRow — this is history, not an
            action — but sized to stay legible beside them: the filename takes
            the free width instead of truncating against empty space. }}
          <div
            class="tio-row-between gap-3 rounded border tio-border-default tio-bg-surface px-3 py-2.5"
            data-test-prior-upload
          >
            <a
              href={{row.asset.signedUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="tio-inline-row gap-2 flex-1 min-w-0 tio-fg-secondary hover:tio-fg-info text-sm"
              title={{row.asset.filename}}
              data-test-prior-upload-link
            >
              <PhFilePdf @size="18" class="shrink-0" />
              <span class="truncate">{{row.asset.filename}}</span>
            </a>
            {{#if row.labelKey}}
              <span class="shrink-0 text-xs tio-fg-muted" data-test-prior-upload-classification>
                {{t row.labelKey}}
              </span>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  {{/if}}
</template>;

export default PriorUploadsList;
